import {useParams} from "react-router-dom";
import {Box, Dialog, DialogTitle, Grid, Tab, Tabs} from "@mui/material";
import React, {SyntheticEvent, useContext, useEffect, useState} from "react";
import {FilterCondition, requestQuery} from "../framework/QueryApi";
import {Button, Card, Toast, ToastContainer} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {Typeahead} from 'react-bootstrap-typeahead';
import './ViewProject.css';
import {ProjectListing} from "./model/ProjectListing";
import {CollaboratorListing} from "./model/CollaboratorListing";
import {CollaboratorInvitationSummary} from "./model/CollaboratorInvitationSummary";
import {InstituteSummary} from "./model/InstituteSummary";
import {AmsDataGrid, AmsGridRowClassNameParams} from "../framework/component/data-grid/AmsDataGrid";
import {collaboratorListColumns} from "./CollaboratorListColumns";
import {createColumnDefinitions} from "./CollaboratorInvitationListColumns";
import {getSummaryStatusLabel} from "./model/ApplicationStatus";
import {CollaboratorType, CollaboratorTypes} from "./model/CollaboratorType";
import {CollaboratorStatus} from "./model/CollaboratorStatus";
import {AxiosResponse} from "axios";
import AppContext from "../framework/AppContext";
import {Role} from "../framework/security/Role";
import apiClient from "../framework/web/ApiClient";
import {isInstituteStatusApproved, InstituteStatusesByValue,} from "./model/InstituteStatus";
import PromoteCollabDialog from "./collaborators/PromoteCollabDialog";
import MessagePanel from "../framework/component/container/MessagePanel";
import {MessagePanelType} from "../framework/component/container/MessagePanelType";


const ViewProject = () => {
  const params = useParams();
  const appContext = useContext(AppContext);
  const [project, setProject] = useState<ProjectListing>();
  const [collaborators, setCollaborators] = useState<CollaboratorListing[]>();
  const [showNonCurrent, setShowNonCurrent] = useState(false);
  const [institutes, setInstitutes] = useState<InstituteSummary[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [collabPromotedToastMessage, setCollabPromotedToastMessage] = useState('')
  const [collabPromotedErrorMessage, setCollabPromotedErrorMessage] = useState('')
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const [selectedInstitute, setSelectedInstitute] = useState(0);
  const [collaboratorType, setCollaboratorType] = useState(CollaboratorType.Collaborator.value);
  const [isDelegate, setDelegate] = useState(false);
  const [error, setError] = useState('');
  const [collaboratorValidationError, setCollaboratorValidationError] = useState('');
  const [instituteValidationError, setInstituteValidationError] = useState('');
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
  const [collaboratorInvitations, setCollaboratorInvitations] = useState<CollaboratorInvitationSummary[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  const [invitationSuccess, setInvitationSuccess] = useState('');
  const [processingInvitation, setProcessingInvitation] = useState<{ id: number; action: 'resend' | 'remove' } | null>(null);
  const [projectHasPi, setProjectHasPi] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const canManageCollaborators = appContext?.userProfile.isInAnyRole(Role.SUPER_AMT, Role.SENIOR_AMT);
  const gridContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: 400,
    maxHeight: 600
  };
  const gridStyle: React.CSSProperties = {marginTop: 2, height: '100%'};

  const fetchCollaborators = () => {
    if (!params.id) {
      return;
    }
    setCollaboratorsLoading(true);
    const filterConditions = [
      new FilterCondition('applicationId', 'eq', params.id),
    ];
    if (!showNonCurrent) {
      filterConditions.push(new FilterCondition('collaboratorStatus', 'eq', CollaboratorStatus.Current.value.toString()));
    }
    requestQuery('collaboratorListing', filterConditions, [], [{
          field: "researcherId",
          direction: "asc"
        }],
        (response: AxiosResponse) => {
          const collabs: CollaboratorListing[] = response.data.data;
          setCollaborators(collabs);
          setProjectHasPi(collabs.some(c => c.collaboratorStatus == CollaboratorStatus.Current.value && c.collaboratorType == CollaboratorType.PI.value));
          setCollaboratorsLoading(false);
        },
        0, 20)
    .catch(e => {
      setError(`Failed to fetch collaborators: ${e}`)
    });
  }

  const fetchInvitations = () => {
    if (!params.id) {
      return Promise.resolve();
    }
    setInvitationsLoading(true);
    setInvitationError('');

    return requestQuery('collaboratorInvitation',
        [new FilterCondition('applicationId', 'eq', params.id),
          new FilterCondition('active', 'eq', 'true')],
        [], [],
        (response: AxiosResponse) => {
          if (response.data.data.length >= 0) {
            setCollaboratorInvitations(response.data?.data ?? []);
            setInvitationsLoading(false);
          } else {
            setInvitationError('Failed to fetch invitations');
            setInvitationsLoading(false);
          }
        },
        0, 20)
    .catch(e => {
      setError(`Failed to fetch collaborator invitation: ${e}`)
    });
  }

  const fetchApplication = () => {
    if (!params.id) {
      return;
    }
    requestQuery('projectListing', [new FilterCondition('applicationId', 'eq', params.id)], [], [],
        (response: AxiosResponse) => {

          if (response.data.data.length > 0) {
            setProject(response.data.data[0])
            document.title = `AMS - Project ${response.data.data[0].title}`;
          } else {
            document.title = 'AMS - Project/application not found';
            setError(`AMS - Project/Application ${params.id} not found`);
            setProject(undefined);
          }
        }, 0, 1)
    .catch(e => {
      setError(`Failed to fetch: ${e}`)
    });
  }

  const fetchInstitutes = () => {
    apiClient.get("/ams/api/v1/institutes", {})
    .then(response => {
      setInstitutes(response.data);
    }).catch(e => {
      setError(`Failed to fetch institutes: ${e}`);
      setInstitutes([]);
    });
  }

  useEffect(() => {
    fetchApplication();
    fetchCollaborators();
    fetchInvitations();
    fetchInstitutes();
  }, [showNonCurrent, params.id]);

  const getInstitutesWithoutLeads = (collabs: CollaboratorListing[]): string[] => {
    if (collabs.length == 0) return []

    const instituteLead = new Map<string, boolean>();
    for (let collab of collabs.filter(c => c.collaboratorStatus == CollaboratorStatus.Current.value)) {
      if (!instituteLead.has(collab.instituteName)) {
        instituteLead.set(collab.instituteName, false);
      }
      if (collab.collaboratorType == CollaboratorType.Lead.value || collab.collaboratorType == CollaboratorType.PI.value) {
        instituteLead.set(collab.instituteName, true);
      }
    }
    const institutesWithoutLeads: string[] = []
    instituteLead.forEach((val, key) => {
      if (!val) {
        institutesWithoutLeads.push(key);
      }
    })
    return institutesWithoutLeads
  }

  const institutesWithoutLeads = getInstitutesWithoutLeads(collaborators ?? []);

  const getUniqueInstitutes = (collabs?: readonly CollaboratorListing[]) => {
    return [...new Set(collabs?.map(c => c.instituteName))];
  }

  const handleAddCollaborator = () => {
    if (isProcessing) return;
    setIsProcessing(true);

    if (collaboratorEmail.length === 0) {
      setCollaboratorValidationError('Please enter a valid email address');
      setIsProcessing(false);
      return;
    } else {
      setCollaboratorValidationError('')
    }
    if (selectedInstitute <= 0) {
      if (instituteValidationError !== 'Please select an institute') {
        setInstituteValidationError('Please select an institute')
      }
      setIsProcessing(false);
      return;
    } else {
      setInstituteValidationError('')
    }

    // Validate we are not adding a non lead to a new institute
    if (collaboratorType === CollaboratorType.Collaborator.value) {
      const selectedInstituteName = institutes.find(i => i.instituteId === selectedInstitute)?.name;
      if (selectedInstituteName &&
        collaborators?.filter(c =>
          c.instituteName === selectedInstituteName &&
          c.collaboratorStatus === CollaboratorStatus.Current.value
        ).length === 0) {
        // Adding a non lead collaborator to a new institute. Not allowed
        setCollaboratorValidationError("A non lead collaborator may not be added to an institute without an active Lead Collaborator");
        setIsProcessing(false);
        return;
      }
    }
    setCollaboratorValidationError("");

    const addCollaboratorRequest = {
      email: collaboratorEmail,
      applicationId: params.id,
      instituteId: selectedInstitute,
      collaboratorType: collaboratorType,
      isDelegate: isDelegate
    }

    apiClient.post("/ams/api/v1/collaborators", addCollaboratorRequest)
    .then(r => {
      fetchCollaborators();
      fetchApplication();
      hideAddCollaborator();
    })
    .catch(e => {
      if (e.status > 400) {
        setCollaboratorValidationError(`Failed to create collaborator`);
        console.log("Failed to create collaborator", e);
      }

      if (e.response.data.errors.length > 0) {
        setCollaboratorValidationError(e.response.data.errors[0].message)
      }
    })
    .finally(() => {
      setIsProcessing(false);
    });
  }

  const handleCollabPromoted = () => {
    setShowPromoteDialog(false);
    fetchCollaborators();
    fetchApplication();
    setCollabPromotedToastMessage('Collaborator was successfully promoted.');
  }
  const handleClosePromoteDialog = (error: string) => {
    if (error.length > 0) {
      fetchCollaborators();
      setCollabPromotedErrorMessage(error);
    }
    setShowPromoteDialog(false);
  }

  const getCollaboratorStatusWarningMessagePanel = () => {
    if (!projectHasPi && institutesWithoutLeads.length > 0) {
      return <div>
        <MessagePanel title={"Missing PI and Lead Collaborator"}
                      panelType={MessagePanelType.WARNING}>This project has no active PI and is
          missing a lead collaborator at one or more institutes.</MessagePanel>
        <hr/>
      </div>
    }
    if (!projectHasPi) {
      return <div>
        <MessagePanel title={"No PI"} panelType={MessagePanelType.WARNING}>This project has no
          active PI.</MessagePanel>
        <hr/>
      </div>
    }
    if (institutesWithoutLeads.length > 0) {
      return <div>
        <MessagePanel title={"Missing Lead Collaborator"} panelType={MessagePanelType.WARNING}>One
          or more institutes associated with this project do not have a lead
          collaborator.</MessagePanel>
        <hr/>
      </div>
    }
  }

  const getPromotionCollabTypes = () => {
    const collabTypes: Array<typeof CollaboratorType[keyof typeof CollaboratorType]> = [];
    if (!projectHasPi) collabTypes.push(CollaboratorType.PI)
    if (institutesWithoutLeads.length > 0) collabTypes.push(CollaboratorType.Lead)
    return collabTypes;
  }

  const handleRemoveInvitation = (invitationId: number) => {
    if (isProcessing) return;
    setIsProcessing(true);

    setProcessingInvitation({ id: invitationId, action: 'remove' });
    setInvitationError('');
    setInvitationSuccess('');
    apiClient.post(`/ams/api/v1/collaborator-invitations/${invitationId}/delete`, {})
    .then(() => {
      setInvitationSuccess('Invitation removed successfully.');
      fetchInvitations();
    })
    .catch(e => {
      console.error('Failed to remove invitation.', e);
      if (e.response?.data?.errors?.length > 0) {
        setInvitationError(e.response.data.errors[0].message);
      } else {
        setInvitationError('Failed to remove invitation. Please try again.');
      }
    })
    .finally(() => {
      setProcessingInvitation(null);
      setIsProcessing(false);
    });
  }

  const handleResendInvitation = (invitationId: number) => {
    if (isProcessing) return;
    setIsProcessing(true);

    setProcessingInvitation({ id: invitationId, action: 'resend' });
    setInvitationError('');
    setInvitationSuccess('');
    apiClient.post(`/ams/api/v1/collaborator-invitations/${invitationId}/resend`, {})
    .then(async (response) => {
      const successMessage = response?.data?.infos?.length > 0
          ? response.data.infos[0].message
          : 'Invitation email sent.';
      setInvitationSuccess(successMessage);
      // Wait for the refetch so the grid shows the new invitationId before re-enabling buttons -
      // otherwise a fast Remove click would target the deactivated old id.
      await fetchInvitations();
    })
    .catch(e => {
      if (e.response?.data?.errors?.length > 0) {
        setInvitationError(e.response.data.errors[0].message);
      } else {
        setInvitationError('Failed to resend invitation');
      }
    })
    .finally(() => {
      setProcessingInvitation(null);
      setIsProcessing(false);
    });
  }

  const hideAddCollaborator = () => {
    setCollaboratorValidationError('');
    setCollaboratorEmail('');
    setSelectedInstitute(0);
    setDelegate(false);
    setCollaboratorType(CollaboratorType.Collaborator.value);
    setShowAddDialog(false);
  }

  const showAddCollaborator = () => {
    fetchInstitutes();
    setShowAddDialog(true);
  }

  const handleInstituteSelect = (selected: any) => {
    if (selected.length === 0) {
      setSelectedInstitute(0);
      setInstituteValidationError('');
      return;
    }

    const selectedInst: InstituteSummary = selected[0];
    if (selectedInst.blacklisted) {
      setInstituteValidationError('Blacklisted institutes or countries cannot be selected');
      setSelectedInstitute(0);
      return;
    }

    if (!isInstituteStatusApproved(selectedInst.status)) {
      setInstituteValidationError(`${InstituteStatusesByValue
      .get(selectedInst.status)?.label ?? 'Invalid'} institutes cannot be selected`);
      setSelectedInstitute(0);
      return;
    }

    setSelectedInstitute(selectedInst.instituteId);
    setInstituteValidationError('');
  }

  const handleCollaboratorSelect = (value: string) => {
    setCollaboratorType(value);
  }

  const getInstituteDisplayName = (institute: InstituteSummary) => {
    if (institute.blacklisted) {
      return institute.name + ' (Blacklisted Country)';
    }

    if (isInstituteStatusApproved(institute.status)) {
      return institute.name;
    }

    return `${institute.name} (${InstituteStatusesByValue.get(institute.status)?.label ?? 'Invalid'})`
  }

  const styleRow = (params: AmsGridRowClassNameParams) => {
    if (params.row.collaboratorStatus == CollaboratorStatus.Ended.value) {
      return "non-current-pi-row";
    } else {
      return "";
    }
  }

  if (!params.id) {
    return (<div>No application id was passed</div>)
  }

  return (

      <Box style={{fontFamily: "Helvetica"}}>
        {error &&
            <div className={"fade alert alert-danger show"}>{error}</div>}
        {getCollaboratorStatusWarningMessagePanel()}
        <h4>Application ID {project?.applicationId}</h4>
        <hr/>
        <Row>
          <Col md={6}>
            <Card className="medium-font" style={{border: 'none'}}>
              <Card.Text>
                <Form>
                  <Form.Group as={Row} className="mb-1">
                    <Form.Label column sm={2}>{projectHasPi ? "PI" : "Previous PI"}</Form.Label>
                    <Col sm={10}>
                      <Form.Control type="text" size="sm" value={project?.piName} readOnly/>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-1">
                    <Form.Label column sm={2}>Institute</Form.Label>
                    <Col sm={10}>
                      <Form.Control type="text" size="sm" value={project?.instituteName} readOnly/>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-1">
                    <Form.Label column sm={2}>Title</Form.Label>
                    <Col sm={10}>
                      <Form.Control type="text" size="sm" value={project?.title} readOnly/>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-1">
                    <Form.Label column sm={2}>Status</Form.Label>
                    <Col sm={10}>
                      <Form.Control type="text" size="sm"
                                    value={project ? getSummaryStatusLabel(project.applicationStatus, project.projectStatus) : ''}
                                    readOnly/>
                    </Col>
                  </Form.Group>
                </Form>
              </Card.Text>
            </Card>
          </Col>
        </Row>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <Tabs value={tabIndex} onChange={(e: SyntheticEvent, value: string) => {
            setTabIndex(parseInt(value))
          }}>
            <Tab label="Collaborators"/>
            <Tab label="Pending Invitations"/>
          </Tabs>
          <div role="tabpanel"
               hidden={tabIndex !== 0}
               id={`simple-tabpanel-${0}`}
               aria-labelledby={`simple-tab-${0}`}>
            {tabIndex === 0 && <Box sx={{p: 3}}>

              <Form.Check className={"mb-2 smaller-font"} label={"Show Previous Collaborators"}
                          checked={showNonCurrent}
                          disabled={!project}
                          onChange={(e) => setShowNonCurrent(e.target.checked)}/>
              <div style={gridContainerStyle}>
                <AmsDataGrid style={gridStyle}
                             columnModel={collaboratorListColumns}
                             rows={collaborators}
                             rowIdFunction={(row: CollaboratorListing) => row.collaboratorId}
                             rowCount={collaborators?.length}
                             loadingFlag={collaboratorsLoading}
                             hideFooter={true}
                             rowClassNameFunction={styleRow}
                             disableSorting={true}/>
              </div>

              {canManageCollaborators &&
                  <Box m={1}>
                    <Button className={"mx-1"} variant="primary" disabled={!project}
                            onClick={showAddCollaborator}>Add Collaborator</Button>
                    {(!projectHasPi || institutesWithoutLeads.length > 0) &&
                        <Button className={"mx-1"} variant="primary" disabled={!project}
                                data-testid="promoteopen"
                                onClick={() => setShowPromoteDialog(true)}>Promote PI/LC</Button>
                    }
                  </Box>
              }
            </Box>}
          </div>
          <div role="tabpanel"
               hidden={tabIndex !== 1}
               id={`simple-tabpanel-${1}`}
               aria-labelledby={`simple-tab-${1}`}>
            {tabIndex === 1 && <Box sx={{p: 3}}>
              {invitationError &&
                  <div className={"fade alert alert-danger show mt-3"}>{invitationError}</div>}
              {invitationSuccess &&
                  <div className={"fade alert alert-success show mt-3"}>{invitationSuccess}</div>}
              <div className="mt-3">
                <div style={gridContainerStyle}>
                  <AmsDataGrid
                      style={gridStyle}
                      columnModel={createColumnDefinitions(canManageCollaborators, processingInvitation, handleResendInvitation, handleRemoveInvitation, institutes)}
                      rows={collaboratorInvitations}
                      rowIdFunction={(row: CollaboratorInvitationSummary) => row.collaboratorInvitationId}
                      rowCount={collaboratorInvitations.length}
                      loadingFlag={invitationsLoading}
                      hideFooter={true}
                      disableSorting={true}/>
                </div>
              </div>
            </Box>}
          </div>
        </Box>

        <Dialog className={"collab-dialog"} open={showAddDialog} fullWidth={true} maxWidth="md">
          <DialogTitle className={"dialog-header"}>Add a Collaborator</DialogTitle>
          <Box sx={{padding: 3,}}>
            <div className="mb-2 mt-3">Collaborator Email</div>
            <Form.Control type="text" className={collaboratorValidationError ? 'is-invalid' : ''}
                          placeholder="Enter Collaborator email address"
                          data-testid="collaboratorEmail"
                          onChange={event => setCollaboratorEmail(event.target.value)}/>
            {collaboratorValidationError && <div data-testid="collaboratorError"
                                                 className={"fade alert alert-danger show"}>{collaboratorValidationError}</div>}
            <div className="mb-2 mt-3">Institute</div>
            <Typeahead inputProps={{id: "institute"}}
                       className={instituteValidationError ? 'is-invalid' : ''}
                       options={institutes}
                       labelKey={(option: any) => getInstituteDisplayName(option)}
                       onChange={handleInstituteSelect}
                       renderMenuItemChildren={(option: any) => {
                         const displayName = getInstituteDisplayName(option);
                         return (
                             <div
                                 className={`typeahead-option${option.blacklisted || !isInstituteStatusApproved(option.status) ? ' typeahead-option-disabled' : ''}`}>
                               {displayName}
                             </div>
                         );
                       }}
            />
            {instituteValidationError &&
                <div data-testid="instituteError" className={"fade alert alert-danger show"}
                     style={{marginTop: '10px'}}>{instituteValidationError}</div>}
            <Grid className={"mt-3"} container columnSpacing={4}>
              {
                CollaboratorTypes.map(ct => (
                    <Grid columns={3}>
                      <Form.Check type={"radio"} name="collabType"
                                  checked={collaboratorType === ct.value}
                                  value={ct.value} label={ct.label}
                                  onChange={(e) => handleCollaboratorSelect(e.target.value)}
                                  data-testid={`collaboratorType-${ct.value}`}/>
                    </Grid>
                ))
              }

              <Grid columns={3}>
                <Form.Check label={"Delegate"} onChange={(e) => setDelegate(e.target.checked)}/>
              </Grid>
            </Grid>
            <div className="d-flex gap-3 center mt-lg-5">
              <Button variant="primary" data-testid="add"
                      onClick={handleAddCollaborator} disabled={isProcessing}>Add</Button>
              <Button variant="secondary" data-testid="cancel"
                      onClick={hideAddCollaborator}>Cancel</Button>
            </div>
          </Box>
        </Dialog>
        {showPromoteDialog &&
            <PromoteCollabDialog collaborators={collaborators}
                                 collabTypes={getPromotionCollabTypes()}
                                 institutes={!projectHasPi ? getUniqueInstitutes(collaborators) : institutesWithoutLeads}
                                 onClose={handleClosePromoteDialog}
                                 onCollabPromoted={handleCollabPromoted}/>}

        <ToastContainer id="viewProjectToastContainer" position="bottom-end"
                        className="p-3 toast-container">
          <Toast id="collabPromotedSuccessToast" onClose={() => setCollabPromotedToastMessage('')}
                 show={collabPromotedToastMessage.length > 0}
                 delay={3000} autohide>
            <Toast.Header id="collabPromotedSuccessToastHeader">
              <strong id="collabPromotedSuccessToastHeaderText" className="me-auto"><i
                  id="collabPromotedSuccessToastHeaderIcon"
                  className="bi bi-check-circle-fill text-success mx-2"/> Collaborator
                Promoted</strong>
            </Toast.Header>
            <Toast.Body
                id="collabPromotedSuccessToastBody">{collabPromotedToastMessage}
            </Toast.Body>
          </Toast>
          <Toast id="collabPromotedErrorToast" onClose={() => setCollabPromotedErrorMessage('')}
                 show={collabPromotedErrorMessage.length > 0}
                 delay={5000} autohide>
            <Toast.Header id="collabPromotedErrorToastHeader">
              <strong id="collabPromotedErrorToastHeaderText" className="me-auto"><i
                  id="collabPromotedErrorToastHeaderIcon"
                  className="bi bi-exclamation-circle-fill text-danger mx-2"/> Error</strong>
            </Toast.Header>
            <Toast.Body
                id="collabPromotedErrorToastBody">{collabPromotedErrorMessage}
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </Box>

  );
}

export default ViewProject;
